import {t, Trans} from '@lingui/macro';
import _, {isEmpty, isNil, isNull} from 'lodash';
import React, {FC, useMemo} from 'react';

import {useInitialEffect} from '../../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {Handler} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownBoxCoordinator} from '../../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {SimpleDropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {PopoverTransferProps} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {useLocalize} from '../../../../../../srcCommon/internationalization/internationalizationContext';
import {DeprecatedSearchDropdown} from '../../../../../components/common/dropdowns/search/deprecatedSearchDropdown';
import {useSyncDropdownSource} from '../../../../../components/common/dropdowns/source/hooks/useSyncDropdownSource';
import {AnswerModel} from '../../../../../models/api/answerModel';
import {SettingsPreferenceItemInheritableProps} from '../../../settingsPreferencesList';

/*
 * Constant.
 */

const defaultNullDisplayName = <Trans id="teammate-prefs-quick-responses-null-name">None</Trans>;

/*
 * Props.
 */

interface QuickResponsesDropdownProps
  extends SettingsPreferenceItemInheritableProps<number | undefined | null> {
  value: number | undefined | null;
  allAnswers: ReadonlyArray<AnswerModel>;
  fetchAllAnswersAndFolders: Handler;
}

/*
 * Component.
 */

export const QuickResponsesDropdown: FC<QuickResponsesDropdownProps> = (props) => {
  const {allAnswers, value, updatePreference} = props;

  useInitialEffect(() => {
    if (isEmpty(allAnswers)) {
      props.fetchAllAnswersAndFolders();
    }
  });

  const localize = useLocalize();
  const currentValue = useMemo(() => {
    const currentAnswer = allAnswers.find((answer) => answer.id === value);
    return currentAnswer?.name ?? defaultNullDisplayName;
  }, [value, allAnswers]);

  const dropdownSource = useSyncDropdownSource<AnswerModel | null>(
    {
      items: [null, ...allAnswers],
      rowFilter: (items, query) =>
        _.chain(items)
          .filter((item, index) => {
            if (isNull(item)) {
              return query ? false : index === 0;
            }
            const lowerCaseQuery = query.toLowerCase();
            const lowerCaseDisplayName = item.name.toLowerCase();
            return lowerCaseDisplayName.includes(lowerCaseQuery);
          })
          .value(),
      renderRow: (item, index) => {
        if (isNull(item)) {
          return (
            <SimpleDropdownItem
              key="quick-responses-item-none"
              onSelect={() => updatePreference(null)}
              isChecked={isNil(value)}
            >
              {defaultNullDisplayName}
            </SimpleDropdownItem>
          );
        }
        const isChecked = value === item.id;
        return (
          <SimpleDropdownItem
            key={`quick-responses-item-${item.id}`}
            onSelect={() => updatePreference(item.id)}
            isChecked={isChecked}
          >
            {item.name}
          </SimpleDropdownItem>
        );
      },
    },
    [],
  );

  const renderDropdown = (popoverProps: PopoverTransferProps) => (
    <DeprecatedSearchDropdown
      {...popoverProps}
      source={dropdownSource}
      title={props.title}
      placeholder={localize(t('preferences-dropdown-search-placeholder')`Search`)}
    />
  );

  return (
    <DropdownBoxCoordinator
      value={currentValue ?? defaultNullDisplayName}
      renderDropdown={renderDropdown}
      noInputMarginRight
    />
  );
};
